import React from 'react';
import { Lead, Task, Opportunity } from '../../types';
import { 
  AlertCircle, TrendingUp, Target, Clock, CheckCircle2,
  ArrowRight, Flame, Calendar, Euro
} from 'lucide-react';

interface TodaysFocusProps {
  leads: Lead[];
  tasks: Task[];
  opportunities: Opportunity[];
  onNavigateToTask: (taskId: string) => void;
  onNavigateToLead: (leadId: string) => void;
  onNavigateToOpportunity: (oppId: string) => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const TodaysFocus: React.FC<TodaysFocusProps> = ({
  leads,
  tasks,
  opportunities,
  onNavigateToTask,
  onNavigateToLead,
  onNavigateToOpportunity
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  // Overdue Tasks
  const overdueTasks = tasks.filter(t => 
    !t.completed && new Date(t.dueDate) < today
  ).slice(0, 3);

  // Hot Leads (Score 80+, in active stages)
  const hotLeads = leads.filter(l => 
    l.score >= 80 && ['interested', 'demo', 'offer', 'closing'].includes(l.stage)
  ).slice(0, 3);

  // Closing Soon (Opportunities closing this week)
  const closingSoon = opportunities.filter(o => {
    if (o.stage === 'won' || o.stage === 'lost') return false;
    const closeDate = new Date(o.expectedCloseDate);
    return closeDate >= today && closeDate <= nextWeek;
  }).slice(0, 3);

  // Today's Tasks
  const todaysTasks = tasks.filter(t => {
    if (t.completed) return false;
    const dueDate = new Date(t.dueDate);
    return dueDate >= today && dueDate < tomorrow;
  }).slice(0, 3);

  // Follow-ups scheduled for today
  const todaysFollowUps = leads.filter(l =>
    l.nextFollowUp && 
    new Date(l.nextFollowUp) >= today &&
    new Date(l.nextFollowUp) < tomorrow
  ).slice(0, 3);

  // Meetings today
  const todaysMeetings = tasks.filter(t =>
    !t.completed &&
    t.type === 'meeting' &&
    new Date(t.dueDate) >= today &&
    new Date(t.dueDate) < tomorrow
  ).slice(0, 3);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-shadow duration-200">
      <div className="flex items-center space-x-2 mb-6">
        <div className="p-2 bg-danger-soft rounded-lg">
          <Flame className="w-5 h-5 text-danger" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-txt">Today's Focus</h3>
          <p className="text-sm text-txt-secondary">Your most important items right now</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Overdue Tasks */}
        {overdueTasks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-danger" />
                <h3 className="font-semibold text-txt">Overdue ({overdueTasks.length})</h3>
              </div>
              <span className="text-xs text-danger font-medium">Needs attention!</span>
            </div>
            <div className="space-y-2">
              {overdueTasks.map(task => (
                <button
                  key={task.id}
                  onClick={() => onNavigateToTask(task.id)}
                  className="w-full text-left p-3 bg-bg-card hover:bg-bg-surface border-l-4 border-l-danger border border-default rounded-lg transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-txt text-sm">{task.title}</div>
                      <div className="text-xs text-danger mt-1">
                        Due: {task.dueDate ? (() => {
                          try {
                            const date = new Date(task.dueDate);
                            return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                          } catch {
                            return 'N/A';
                          }
                        })() : 'N/A'}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-danger opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hot Leads */}
        {hotLeads.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-success" />
                <h3 className="font-semibold text-txt">Hot Leads ({hotLeads.length})</h3>
              </div>
              <span className="text-xs text-success font-medium">High potential!</span>
            </div>
            <div className="space-y-2">
              {hotLeads.map(lead => (
                <button
                  key={lead.id}
                  onClick={() => onNavigateToLead(lead.id)}
                  className="w-full text-left p-3 bg-bg-card hover:bg-bg-surface border-l-4 border-l-accent border border-default rounded-lg transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-txt text-sm">
                        {lead.firstName} {lead.lastName} - {lead.company}
                      </div>
                      <div className="text-xs text-success mt-1">
                        Score: {lead.score}% • {lead.stage}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-success opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Closing Soon */}
        {closingSoon.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-txt">Closing This Week ({closingSoon.length})</h3>
              </div>
              <span className="text-xs text-accent font-medium">Push to close!</span>
            </div>
            <div className="space-y-2">
              {closingSoon.map(opp => (
                <button
                  key={opp.id}
                  onClick={() => onNavigateToOpportunity(opp.id)}
                  className="w-full text-left p-3 bg-bg-card hover:bg-bg-surface border-l-4 border-l-txt-muted border border-default rounded-lg transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-txt text-sm">{opp.title}</div>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-accent">
                          {formatCurrency(opp.value || 0)}
                        </span>
                        <span className="text-xs text-txt-secondary">
                          Close: {opp.expectedCloseDate ? (() => {
                            try {
                              const date = new Date(opp.expectedCloseDate);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch {
                              return 'N/A';
                            }
                          })() : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Today's Tasks */}
        {todaysTasks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-txt">Due Today ({todaysTasks.length})</h3>
              </div>
            </div>
            <div className="space-y-2">
              {todaysTasks.map(task => (
                <button
                  key={task.id}
                  onClick={() => onNavigateToTask(task.id)}
                  className="w-full text-left p-3 bg-bg-card hover:bg-bg-surface border-l-4 border-l-txt-muted border border-default rounded-lg transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-txt text-sm">{task.title}</div>
                      <div className="text-xs text-accent mt-1 capitalize">
                        {task.type} • {task.priority} priority
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Follow-ups Today */}
        {todaysFollowUps.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-txt">Follow-ups ({todaysFollowUps.length})</h3>
              </div>
            </div>
            <div className="space-y-2">
              {todaysFollowUps.map(lead => (
                <button
                  key={lead.id}
                  onClick={() => onNavigateToLead(lead.id)}
                  className="w-full text-left p-3 bg-bg-card hover:bg-bg-surface border-l-4 border-l-accent border border-default rounded-lg transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-txt text-sm">
                        {lead.firstName} {lead.lastName}
                      </div>
                      <div className="text-xs text-accent mt-1">
                        {lead.company} • {lead.stage}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* All Clear State */}
        {overdueTasks.length === 0 && hotLeads.length === 0 && closingSoon.length === 0 && todaysTasks.length === 0 && todaysFollowUps.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-3" />
            <h3 className="font-semibold text-txt mb-1">You're all caught up!</h3>
            <p className="text-sm text-txt-secondary">No urgent items right now. Great work!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysFocus;

